import { formatISO } from 'date-fns';
import debounce from 'debounce';
import { Autocomplete, SearchIcon } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { HistoryDatepickerWithModal } from 'reusable/historyDatepickerWithModal/HistoryDatepickerWithModal';
import { TableSearchContainer } from 'reusable/tableSearch/TableSearchContainer';
import { HistoryActionAutocomplete } from 'reusable/historyActionAutocomplete/HistoryActionAutocomplete';
import { HistoryList } from 'ui/historyList/HistoryList';
import { GroupTypeEnum } from 'api/groups/groups.enum';
import { transformTreeToFlatArray } from 'helpers/transformTreeToFlatArray';
import { useGroupHistoryQuery } from '../hooks/useGroupHistoryQuery';
import { OrganizationsTreeSelect } from 'ui/organizationsTreeSelect/OrganizationsTreeSelect';
import { filterQueryFromPagination } from 'helpers/filterQueryFromPagination';
import * as Styled from '../GroupHistory.styles';
import { HistoryActionEnum } from 'core/global.enum';

import { BaseHistoryProps } from './BaseHistory.types';

export const BaseHistory = ({
  group,
  groupHistoryResponse,
  page,
  rowsPerPage,
  setPage,
  setRowsPerPage,
  isLoading,
  isFetching,
  onClearHistoryQuery,
  searchQuery,
  setSearchQuery,
  organizations,
  owners,
  setOwnersQuery,
  isLoadingOwners,
}: BaseHistoryProps) => {
  const { formatMessage } = useLocale();
  const { setGroupHistoryQuery, query: groupHistoryQuery } = useGroupHistoryQuery();

  const ownersOptions =
    owners?.map((owner) => ({
      label: `${owner.nazwisko || ''} ${owner.imie || ''}`,
      value: owner.id,
      number: owner.nrEwidencyjny,
    })) || [];

  const handleOwnerSearch = debounce((search: string) => {
    setOwnersQuery(search);
  }, 600);

  return (
    <HistoryList
      data={groupHistoryResponse?.data || []}
      isLoading={isLoading}
      filters={{
        gridTemplateColumns: '1fr 1fr 1fr',
        isClearButtonVisible:
          Object.values(filterQueryFromPagination(groupHistoryQuery)).some((query) => query) || !!searchQuery,
        onClearFilters: () => onClearHistoryQuery(),
        render: () => (
          <>
            <HistoryDatepickerWithModal
              dates={[
                groupHistoryQuery.dataOd ? new Date(groupHistoryQuery.dataOd) : null,
                groupHistoryQuery.dataDo ? new Date(groupHistoryQuery.dataDo) : null,
              ]}
              setDates={(dates) =>
                setGroupHistoryQuery({
                  dataOd: dates[0] ? formatISO(dates[0]) : undefined,
                  dataDo: dates[1] ? formatISO(dates[1]) : undefined,
                })
              }
            />

            <HistoryActionAutocomplete
              selectedAction={groupHistoryQuery.akcja}
              onChangeAction={(action) => setGroupHistoryQuery({ akcja: action })}
              historyType="groups"
            />

            {group?.typ === GroupTypeEnum.PERSONALNA && (
              <Autocomplete
                onChange={(_, option) => setGroupHistoryQuery({ posiadacz: option?.value as string | undefined })}
                onInputChange={(e, value) => e && handleOwnerSearch(value)}
                filterOptions={(x) => x}
                value={ownersOptions.find(({ value }) => groupHistoryQuery.posiadacz === value) || null}
                options={ownersOptions}
                loading={isLoadingOwners}
                renderOption={(props, option) =>
                  option.value ? (
                    <li {...props} key={`${option.label}-${option.value}`}>
                      <Styled.OptionNumber>
                        {formatMessage({ id: AppMessages['tableSearch.owner.number'] })}{' '}
                        {owners ? owners.find((owner) => owner.id === option.value)?.nrEwidencyjny || 0 : ''}
                      </Styled.OptionNumber>

                      <Styled.OptionLabel>{option.label}</Styled.OptionLabel>
                    </li>
                  ) : null
                }
                loadingText={formatMessage({ id: AppMessages['common.loading'] })}
                noOptionsText={formatMessage({ id: AppMessages['common.notFound'] })}
                placeholder={formatMessage({ id: AppMessages['group.history.search.owner'] })}
                inputVariant="outlined"
                inputSize="big"
                isOptionEqualToValue={(option, value) => option.value === value.value}
                popupIcon={<SearchIcon />}
                stopRotatePopupIndicator
              />
            )}

            {group?.typ === GroupTypeEnum.STRUKTURALNA && (
              <OrganizationsTreeSelect
                inputVariant="outlined"
                inputSize="big"
                onChange={(val) => setGroupHistoryQuery({ komorka: val?.id })}
                value={
                  organizations
                    ? transformTreeToFlatArray(organizations).find(
                        (organization) => organization.id === groupHistoryQuery.komorka,
                      )
                    : undefined
                }
                placeholder={formatMessage({ id: AppMessages['group.history.search.cell'] })}
              />
            )}

            <TableSearchContainer
              withoutToggle
              inputPlaceholder={formatMessage({ id: AppMessages['group.history.search.executor'] })}
              searchBy="users"
              tableSearchQuery={searchQuery}
              setTableSearchQuery={setSearchQuery}
              selectedValue={groupHistoryQuery.query}
              setSelectedValue={(option) => setGroupHistoryQuery({ query: option || undefined })}
              inputSize="big"
            />
          </>
        ),
      }}
      pagination={{
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        count: groupHistoryResponse?.count || 0,
      }}
      renderHistoryItemBody={(history) => {
        const isOrganizationAddedBySystem =
          history.akcja === HistoryActionEnum.ZEDYTOWANO &&
          !history.wykonawca &&
          history?.komorka?.new &&
          history?.komorka?.old &&
          history?.komorka?.new.length > history?.komorka?.old.length;
        const isOrganizationDeletedBySystem =
          history.akcja === HistoryActionEnum.ZEDYTOWANO &&
          !history.wykonawca &&
          history?.komorka?.new &&
          history?.komorka?.old &&
          history?.komorka?.new.length < history?.komorka?.old.length;

        return (
          <>
            {history.wykonawca && (
              <HistoryList.ActionBodyItem
                title={formatMessage({ id: AppMessages['group.history.descriptionTitle.executor'] })}
                value={`${history.wykonawca?.nazwisko || ''} ${history.wykonawca?.imie || ''}`}
              />
            )}

            {history.nazwa && (
              <HistoryList.ActionModifiedFieldBodyItem
                title={formatMessage({ id: AppMessages['group.history.descriptionTitle.name'] })}
                oldValue={history.nazwa.old}
                newValue={history.nazwa.new}
              />
            )}

            {history.opis && (
              <HistoryList.ActionModifiedLongFieldBodyItem
                title={formatMessage({ id: AppMessages['group.history.descriptionTitle.description'] })}
                oldValue={history.opis.old}
                newValue={history.opis.new}
              />
            )}

            {history.komorka && (
              <HistoryList.ActionModifiedFieldBodyItem
                title={formatMessage({ id: AppMessages['group.history.descriptionTitle.cell'] })}
                oldValue={history.komorka.old}
                newValue={history.komorka.new}
              />
            )}

            {isOrganizationAddedBySystem && (
              <HistoryList.ActionBodyItem
                title={formatMessage({ id: AppMessages['group.history.descriptionTitle.reason'] })}
                value={formatMessage({ id: AppMessages['group.history.descriptionTitle.reasonAdded'] })}
              />
            )}

            {isOrganizationDeletedBySystem && (
              <HistoryList.ActionBodyItem
                title={formatMessage({ id: AppMessages['group.history.descriptionTitle.reason'] })}
                value={formatMessage({ id: AppMessages['group.history.descriptionTitle.reasonDeleted'] })}
              />
            )}

            {history.dodaniPosiadacze && (
              <HistoryList.ActionListdBodyItem
                title={formatMessage({ id: AppMessages['group.history.descriptionTitle.addedOwners'] })}
                items={history.dodaniPosiadacze}
                shouldScrollToTop={!!groupHistoryQuery.posiadacz && !isFetching}
                renderItemBody={(owner) => (
                  <HistoryList.RenderedActionListdBodyItem
                    numberPrefix={formatMessage({ id: AppMessages['common.registrationNumber'] })}
                    number={owner.nrEwidencyjny}
                    value={`${owner.nazwisko} ${owner.imie}`}
                  />
                )}
              />
            )}

            {history.usunieciPosiadacze && (
              <HistoryList.ActionListdBodyItem
                title={formatMessage({ id: AppMessages['group.history.descriptionTitle.removedOwners'] })}
                items={history.usunieciPosiadacze}
                shouldScrollToTop={!!groupHistoryQuery.posiadacz && !isFetching}
                renderItemBody={(owner) => (
                  <HistoryList.RenderedActionListdBodyItem
                    numberPrefix={formatMessage({ id: AppMessages['common.registrationNumber'] })}
                    number={owner.nrEwidencyjny}
                    value={`${owner.nazwisko} ${owner.imie}`}
                  />
                )}
              />
            )}

            {history.powod && (
              <HistoryList.ActionBodyItem
                title={formatMessage({ id: AppMessages['group.history.descriptionTitle.reason'] })}
                value={history.powod}
              />
            )}
          </>
        );
      }}
    />
  );
};
